'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Bot, ArrowRight, CircleDot, BookOpen, MessageSquare,
  Briefcase, Heart, Coins, GraduationCap, Users,
  Building2, Plane, Baby,
  type LucideIcon
} from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  CircleDot, BookOpen, MessageSquare,
  Briefcase, Heart, Coins, GraduationCap, Users,
  Building2, Plane, Baby,
};

interface TopicCard {
  icon: string;
  label: string;
  question: string;
}

interface TrustItem {
  icon: string;
  text: string;
}

interface AskAstrologerBoxProps {
  locale: string;
  labels: {
    heading: string;
    subline: string;
    placeholder: string;
    button: string;
  };
  trust: TrustItem[];
  topics: TopicCard[];
}

const MAX_QUESTION_LENGTH = 500;

export function AskAstrologerBox({ locale, labels, trust, topics }: AskAstrologerBoxProps) {
  const router = useRouter();
  const [question, setQuestion] = useState('');

  const aiPath = locale === 'hi' ? '/hi/ai-astrologer' : '/ai-astrologer';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = question.trim().slice(0, MAX_QUESTION_LENGTH);
    if (trimmed) {
      router.push(`${aiPath}?q=${encodeURIComponent(trimmed)}`);
    } else {
      router.push(aiPath);
    }
  };

  return (
    <div className="rounded-3xl border border-white/[0.16] bg-gradient-to-br from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] backdrop-blur-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_30px_80px_-20px_rgba(0,40,120,0.45),0_0_50px_rgba(120,80,230,0.12)] p-5 sm:p-8">
      {/* Heading row */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-white/10 border border-white/20 rounded-xl shrink-0">
          <Bot className="w-5 h-5 text-[#5ab4ff]" />
        </div>
        <h2 className="text-xl sm:text-2xl font-heading font-semibold text-white">
          {labels.heading}
        </h2>
      </div>

      {/* 8px: subline */}
      <p className="text-xs sm:text-[13px] text-[rgba(220,236,255,0.85)] leading-relaxed mt-2">
        {labels.subline}
      </p>

      {/* 12px: trust row */}
      <div
        className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[rgba(220,236,255,0.85)] mt-3"
        data-testid="ask-trust-row"
      >
        {trust.map((item, idx) => {
          const Icon = ICON_MAP[item.icon] || CircleDot;
          return (
            <span key={idx} className="inline-flex items-center gap-1.5" data-testid={`ask-trust-${idx}`}>
              <Icon className="w-3.5 h-3.5 text-[#8dffc9] shrink-0" />
              {item.text}
            </span>
          );
        })}
      </div>

      {/* 20px: input and button row (stacked on mobile with 12px between) */}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mt-5" data-testid="ask-astrologer-form">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={MAX_QUESTION_LENGTH}
          placeholder={labels.placeholder}
          className="flex-1 h-14 bg-[rgba(2,6,24,0.65)] border border-white/25 rounded-full px-6 text-sm sm:text-base text-white placeholder-[rgba(220,236,255,0.72)] focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all"
        />
        <button
          type="submit"
          className="h-14 px-8 inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#03163d] font-semibold text-sm sm:text-base hover:bg-white/90 shadow-[0_0_35px_rgba(150,165,255,0.6)] transition-all transform hover:-translate-y-0.5 active:scale-95 cursor-pointer shrink-0"
        >
          <span>{labels.button}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      {/* 24px: Suggested topics label */}
      <div className="mt-6">
        <div className="text-xs font-semibold tracking-wider uppercase text-[rgba(220,236,255,0.85)]">
          {locale === 'hi' ? 'सुझाए गए विषय' : 'Suggested Topics'}
        </div>
        {/* 12px: topic cards, 16px gap desktop, 12px gap mobile, 2 cols mobile, 4 cols desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3" data-testid="ask-topics-grid">
          {topics.map((item, idx) => {
            const Icon = ICON_MAP[item.icon] || CircleDot;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setQuestion(item.question)}
                className="h-full min-h-[96px] flex flex-col justify-between items-start text-left p-3.5 sm:p-4 rounded-2xl border border-white/[0.18] bg-gradient-to-br from-white/[0.08] to-white/[0.03] bg-[rgba(10,18,50,0.6)] hover:bg-white/[0.12] hover:border-white/40 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8dffc9] transition-all cursor-pointer group w-full"
                data-testid={`ask-topic-${idx}`}
              >
                <div className="flex items-center gap-2.5 mb-2 w-full">
                  <div className="p-1.5 rounded-lg bg-white/10 border border-white/15 group-hover:border-[#5ab4ff]/50 transition-colors shrink-0">
                    <Icon className="w-4 h-4 text-[#5ab4ff]" />
                  </div>
                  <span className="text-[15px] font-bold text-white truncate">
                    {item.label}
                  </span>
                </div>
                <p className="text-[13px] text-[rgba(220,236,255,0.82)] group-hover:text-white line-clamp-2 leading-snug">
                  {item.question}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
